import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { parseSqlError } from '@/shared/lib/octo/sqlError';
import { Dialog } from './Dialog';
import { Button } from './Button';
import { Alert } from './Alert';
import { useDialogStore } from './dialogStore';

/** dialogStore'un tek görsel karşılığı: onay ve hata diyalogları. Enter = onay, Esc = iptal. */
export function DialogHost() {
  const { t } = useTranslation();
  const confirm = useDialogStore((s) => s.confirm);
  const error = useDialogStore((s) => s.error);
  const resolveConfirm = useDialogStore((s) => s.resolveConfirm);
  const resolveError = useDialogStore((s) => s.resolveError);
  return (
    <>
      <Dialog
        open={!!confirm}
        onOpenChange={(o) => !o && resolveConfirm(false)}
        title={
          confirm?.title ?? (confirm?.kind === 'delete' ? t('dialog.deleteTitle') : t('dialog.confirmTitle'))
        }
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => resolveConfirm(false)}>
              {confirm?.cancelText ?? t('common:Cancel')}
            </Button>
            <Button
              variant={confirm?.kind === 'delete' ? 'danger' : 'primary'}
              autoFocus
              onClick={() => resolveConfirm(true)}
            >
              {confirm?.okText ?? (confirm?.kind === 'delete' ? t('common:Delete') : t('dialog.ok'))}
            </Button>
          </>
        }
      >
        <div className="text-sm">{confirm?.message}</div>
      </Dialog>
      <Dialog
        open={!!error}
        onOpenChange={(o) => !o && resolveError()}
        title={error?.title ?? t('dialog.errorTitle')}
        size={error?.sql ? 'lg' : 'md'}
        footer={
          <Button autoFocus onClick={resolveError}>
            {t('dialog.ok')}
          </Button>
        }
      >
        <Alert tone={error?.tone ?? 'danger'}>
          {/* Çok satırlı mesaj (ör. içe aktarmada satır satır hatalar) satır yapısını korur. */}
          <span className="whitespace-pre-line">{error?.message}</span>
        </Alert>
        {error?.detail ? <TechnicalDetail key={error.id} detail={error.detail} /> : null}
        {error?.sql ? (
          <pre className="bg-bg-muted mt-3 max-h-60 overflow-auto rounded-md p-2 font-mono text-xs whitespace-pre-wrap">
            {error.sql}
          </pre>
        ) : null}
      </Dialog>
    </>
  );
}

/**
 * Hatanın teknik kısmı (Konum / Satır / sunucu ayrıntısı) kapalı gelir, "Teknik ayrıntı" ile
 * açılır (#463): kullanıcıyı ilgilendiren iş mesajı; konum ve satır hatayı arayan için.
 * Konum/Satır kalıbı ayrıştırılıp etiketli gösterilir, kalıba uymayan metin olduğu gibi.
 */
function TechnicalDetail({ detail }: { detail: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const text = detail.replace(/\r\n?/g, '\n').trim();
  // Ayrıntı "Mesaj :" öncesi kısımdır; kalıbı çözmek için sahte bir mesaj satırı eklenir.
  const parsed = parseSqlError(`${text}\nMesaj : -`);
  const rows = parsed.structured
    ? (
        [
          [t('errors.location'), parsed.procedure],
          [t('errors.line'), parsed.line],
          [t('errors.code'), parsed.code],
        ] as const
      ).filter(([, v]) => !!v)
    : [];
  const rest = parsed.structured ? [parsed.context, parsed.extra].filter(Boolean).join('\n') : text;
  return (
    <div className="border-border mt-3 border-t pt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="text-fg-muted hover:text-fg flex items-center gap-1 text-xs font-medium"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {t('errors.technical')}
      </button>
      {open ? (
        <div className="mt-2 space-y-2">
          {rows.length ? (
            <dl className="text-fg-muted grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-[11.5px]">
              {rows.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-fg-subtle">{k}</dt>
                  <dd className="break-all">{v}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {rest ? (
            <pre className="bg-bg-muted text-fg-muted max-h-40 overflow-auto rounded-md p-2 text-xs whitespace-pre-wrap">
              {rest}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
